import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Form,
    Input,
    Upload,
    Button,
    Checkbox,
    message,
    Select,
    Spin,
    Alert,
} from "antd";
import {
    UploadOutlined,
    FileOutlined,
    CheckCircleOutlined,
    FolderAddOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

export default function ImportModal({ visible, folders, onClose, onSubmit, loading }) {
    const { t } = useTranslation("referentiel");
    const [form] = Form.useForm();

    const [fileList, setFileList] = useState([]);
    const [titleTouched, setTitleTouched] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    // Folder creation flow (Select + option "Add new")
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");

    const UNCATEGORIZED_VALUE = "__uncategorized__";
    const ADD_NEW_FOLDER_VALUE = "__add_new_folder__";

    const ACCEPTED_FORMATS = [".pdf", ".docx", ".txt"];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    // Helpers
    const normalizeFolderName = (s) =>
        (s || "").trim().replace(/\s+/g, " ");

    const isReservedFolderValue = (v) =>
        v === UNCATEGORIZED_VALUE || v === ADD_NEW_FOLDER_VALUE;

    const resetAll = () => {
        form.resetFields();
        setFileList([]);
        setTitleTouched(false);
        setUploadError(null);
        setCreatingFolder(false);
        setNewFolderName("");
    };

    useEffect(() => {
        if (!visible) resetAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    // Build select options:
    // - Always include "Uncategorized"
    // - Include existing folders (value = title, not key, so DB stores readable folder)
    // - Include "Add folder" action
    const folderOptions = useMemo(() => {
        const map = new Map();

        map.set(UNCATEGORIZED_VALUE, {
            label: t("newFolder.uncategorized"),
            value: UNCATEGORIZED_VALUE,
        });

        (folders || []).forEach((f) => {
            const title = normalizeFolderName(f?.title);
            if (!title) return;

            // avoid duplicating uncategorized if backend returns it
            const lower = title.toLowerCase();
            if (lower === "non classé" || lower === "non classe" || lower === "uncategorized") return;

            // use title as value
            if (!map.has(title)) {
                map.set(title, { label: title, value: title });
            }
        });

        // Add action option at the end
        map.set(ADD_NEW_FOLDER_VALUE, {
            label: (
                <span>
          <FolderAddOutlined /> {t("newFolder.addNew")}
        </span>
            ),
            value: ADD_NEW_FOLDER_VALUE,
        });

        return Array.from(map.values());
    }, [folders, t]);

    const validateFile = (file) => {
        const isValidFormat = ACCEPTED_FORMATS.some((ext) =>
            (file.name || "").toLowerCase().endsWith(ext)
        );

        if (!isValidFormat) {
            const err = t("errors.invalidFormat");
            message.error(err);
            setUploadError(err);
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            const err = t("errors.fileTooLarge");
            message.error(err);
            setUploadError(err);
            return false;
        }

        setUploadError(null);
        return true;
    };

    const handleFileChange = ({ fileList: nextList }) => {
        const file = nextList?.[0]?.originFileObj;

        if (file && !validateFile(file)) {
            setFileList([]);
            return;
        }

        setFileList(nextList);

        if (!file) {
            if (!titleTouched) form.setFieldsValue({ title: "" });
            return;
        }

        const name = (file.name || "").trim();
        const base = name.replace(/\.[^/.]+$/, "").trim();
        const currentTitle = (form.getFieldValue("title") || "").trim();

        if (!titleTouched && !currentTitle) {
            form.setFieldsValue({ title: base || name || "" });
        }
    };

    const handleFolderSelectChange = (value) => {
        if (value === ADD_NEW_FOLDER_VALUE) {
            setCreatingFolder(true);
            setNewFolderName("");
            // clear current selection (user must create then we set it)
            form.setFieldsValue({ folder: undefined });
            return;
        }
        // user chose a real folder or uncategorized
        setCreatingFolder(false);
        setNewFolderName("");
    };

    const confirmCreateFolder = () => {
        const name = normalizeFolderName(newFolderName);
        if (!name) {
            message.error(t("newFolder.newRequired"));
            return;
        }
        if (isReservedFolderValue(name)) {
            message.error(t("newFolder.newInvalid"));
            return;
        }

        // set as selected value
        form.setFieldsValue({ folder: name });
        setCreatingFolder(false);
        setNewFolderName("");
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            const fileObj = fileList?.[0]?.originFileObj;
            if (!fileObj) {
                message.error(t("errors.noFile"));
                return;
            }

            const title = (values.title || "").trim();
            if (!title) {
                message.error(t("errors.requiredTitle"));
                return;
            }

            const rawFolder = values.folder;
            const folderValue = normalizeFolderName(rawFolder);

            // ✅ Si c'est "Non classé", on envoie explicitement une chaîne vide
            const folder =
                !folderValue ||
                folderValue === UNCATEGORIZED_VALUE ||
                folderValue === ADD_NEW_FOLDER_VALUE
                    ? ""  // Chaîne vide pour "Non classé"
                    : folderValue;

            const formData = new FormData();
            formData.append("title", title);

            // ✅ On envoie toujours le champ folder, même vide
            formData.append("folder", folder);

            formData.append("is_active", values.is_active ? "true" : "false");
            formData.append("file_usage", "repository_item");
            formData.append("uploaded_file", fileObj);

            onSubmit(formData);
        } catch (err) {
            console.error(err);
            message.error(t("errors.validation"));
        }
    };

    const hasFile = fileList.length > 0;
    const fileName = fileList[0]?.name;

    const uploadHelp = uploadError
        ? uploadError
        : hasFile
            ? (
                <span className="referentiel-upload-success">
                    <CheckCircleOutlined /> {t("form.fileSelected")}
                </span>
            )
            : t("form.fileHint");

    return (
        <Modal
            className="referentiel-import-modal"
            title={
                <div className="referentiel-import-title">
                    <span className="referentiel-import-title-icon">
                        <UploadOutlined />
                    </span>
                    <div>
                        <strong>{t("modal.title")}</strong>
                        <span>{t("modal.subtitle", "PDF, DOCX ou TXT, 10MB max")}</span>
                    </div>
                </div>
            }
            open={visible}
            onCancel={onClose}
            onOk={handleOk}
            confirmLoading={loading}
            centered
            okText={t("modal.ok")}
            cancelText={t("modal.cancel")}
            width={560}
            closable={!loading}
            maskClosable={!loading}
            okButtonProps={{ disabled: !hasFile, className: "verdictia-primary-btn" }}
        >
            <Spin spinning={loading} tip={t("modal.loading")}>
                <Form
                    className="referentiel-import-form"
                    layout="vertical"
                    form={form}
                    initialValues={{
                        is_active: true,
                        folder: UNCATEGORIZED_VALUE,
                    }}
                >
                    {/* 1) Upload */}
                    <Form.Item
                        className="referentiel-file-field"
                        label={t("form.file")}
                        required
                        validateStatus={uploadError ? "error" : hasFile ? "success" : ""}
                        help={uploadHelp}
                    >
                        <Upload.Dragger
                            className="referentiel-upload-dropzone"
                            accept=".pdf,.docx,.txt"
                            fileList={fileList}
                            onChange={handleFileChange}
                            beforeUpload={() => false}
                            maxCount={1}
                            disabled={loading || hasFile}
                            showUploadList={{
                                showRemoveIcon: !loading,
                                showPreviewIcon: false,
                            }}
                        >
                            <p className="referentiel-upload-icon">
                                <UploadOutlined />
                            </p>
                            <p className="referentiel-upload-text">
                                {hasFile ? t("form.fileUploaded") : t("form.selectFileButton")}
                            </p>
                            <p className="referentiel-upload-hint">{t("form.fileHint")}</p>
                        </Upload.Dragger>
                    </Form.Item>

                    {/* 2) Title */}
                    <Form.Item
                        name="title"
                        label={t("form.title")}
                        rules={[{ required: true, message: t("errors.requiredTitle") }]}
                        extra={
                            !titleTouched && hasFile && fileName ? (
                                <span className="referentiel-field-note">
                                    <FileOutlined /> {t("form.autoFilled")}
                                </span>
                            ) : (
                                t("form.titleExtra")
                            )
                        }
                    >
                        <Input
                            placeholder={t("form.titlePlaceholder")}
                            onChange={() => setTitleTouched(true)}
                            size="large"
                            disabled={loading}
                        />
                    </Form.Item>

                    {/* 3) Folder select + add new */}
                    <Form.Item
                        name="folder"
                        label={t("form.folder")}
                        extra={t("form.folderExtra")}
                    >
                        <Select
                            showSearch
                            placeholder={t("form.folderPlaceholder")}
                            size="large"
                            options={folderOptions}
                            onChange={handleFolderSelectChange}
                            filterOption={(input, option) =>
                                String(option?.label ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            disabled={loading}
                            notFoundContent={t("form.noFolder")}
                        />
                    </Form.Item>

                    {creatingFolder && (
                        <Form.Item label={t("newFolder.newLabel")}>
                            <Input
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder={t("newFolder.newPlaceholder")}
                                disabled={loading}
                                onPressEnter={confirmCreateFolder}
                                addonAfter={
                                    <Button
                                        type="link"
                                        onClick={confirmCreateFolder}
                                        disabled={loading}
                                        style={{ padding: 0, height: "auto" }}
                                    >
                                        {t("newFolder.newConfirm")}
                                    </Button>
                                }
                            />
                        </Form.Item>
                    )}

                    {/* 4) Active */}
                    <Form.Item
                        name="is_active"
                        valuePropName="checked"
                        extra={t("form.activeExtra")}
                    >
                        <Checkbox disabled={loading}>{t("form.activeFile")}</Checkbox>
                    </Form.Item>

                    {/* Info */}
                    {hasFile && (
                        <Alert
                            className="referentiel-import-alert"
                            message={t("info.readyToImport")}
                            description={t("info.importDescription", {
                                name: form.getFieldValue("title") || fileName,
                            })}
                            type="info"
                            showIcon
                        />
                    )}
                </Form>
            </Spin>
        </Modal>
    );
}
